<script>
import Validatable from 'vuetify/lib/mixins/validatable';

// FIXME: vuetify compability
import ThemeLoader from '@/components/common/ThemeLoader.vue';

export default defineComponent({
  name: 'BaseInput',
  components: {
    ThemeLoader,
  },

  mixins: [Validatable],

  inject: ['isIosDevice'],

  inheritAttrs: false,

  props: {
    /*
    // vuetify Validatable mixin has it
    value: {
      type: [String, Number],
      default: null,
    },*/
    loading: {
      type: Boolean,
      default: false,
    },
    textarea: {
      type: Boolean,
      default: false,
    },
    manualResize: {
      type: Boolean,
      default: false,
    },
    errorMessages: {
      type: Array,
      default: null,
    },
    hideErrors: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { attrs }) {
    const iosZoomThreshold = 16; // px
    const targetFontSize = 13; // px
    const fontFactor = targetFontSize / iosZoomThreshold;

    const injectedErrors = inject(`errorMessages:${attrs.name}`, null);

    return {
      iosZoomThreshold,
      targetFontSize,
      fontFactor,

      injectedErrors,
    };
  },

  computed: {
    errors() {
      return this.errorMessages || this.injectedErrors || this.validations;
    },

    externalListeners() {
      const listeners = { ...this.$listeners, change: this.onChange, input: this.onInput };

      return listeners;
    },

    formControlTag() {
      return this.textarea ? 'textarea' : 'input';
    },
  },

  watch: {
    value(newValue) {
      // т.к. (скорее всего) vue цепляется за тэг input, а мы используем обёртку component, то менять через аттрибут value не можем, делаем это
      this.$refs.inputRef.value = newValue;

      // onInput не отрабатывает внешние изменения, следим за размером здесь
      this.shakeHeight();
    },
    '$attrs.rows': function () {
      this.shakeHeight();
    },
  },

  mounted() {
    if (this.value != null) {
      this.$refs.inputRef.value = this.value;
    }

    this.$nextTick(() => this.shakeHeight());
  },

  methods: {
    onInput(event) {
      const value = event.target.value;

      const inputExternalCallback = this.$listeners?.input;
      if (inputExternalCallback) {
        inputExternalCallback(value);
      }
      else {
        this.$emit('input', value);
      }
    },

    // совместимость с vuetify text-field, т.к он вызывает change с новым значением, а не с Event
    onChange(event) {
      const value = event.target.value;

      const changeExternalCallback = this.$listeners?.change;
      if (changeExternalCallback) {
        changeExternalCallback(value);
      }
      else {
        this.$emit('change', value);
      }
    },

    focus() {
      this.$refs.inputRef?.focus();
    },

    shakeHeight() {
      if (!this.textarea || this.manualResize)
        return;
      // height: auto некорректно работает с одной строкой (в чате, например)
      this.$refs.inputRef.style.height = `${Number.parseInt(this.$attrs.rows) || 1}lh`;
      this.$refs.inputRef.style.height = `${this.$refs.inputRef.scrollHeight}px`;

      if (this.isIosDevice) {
        this.$refs.inputRef.style.marginTop = `-${this.$refs.inputRef.scrollHeight * (1 - this.fontFactor) / 2}px`;
        this.$refs.inputRef.style.marginBottom = `-${this.$refs.inputRef.scrollHeight * (1 - this.fontFactor) / 2}px`;
      }
    },
  },
});
</script>

<template>
  <div :class="$style.container">
    <slot name="top" />

    <div v-show="$attrs.type !== 'hidden'" :class="$style.wrapper">
      <slot name="prepend" :icon-class="$style.icon" />
      <div :class="$style.content">
        <slot name="prepend-inner" />

        <component
          :is="formControlTag"
          ref="inputRef"
          v-bind="$attrs"
          :disabled="isDisabled"
          :placeholder="$attrs.label || $attrs.placeholder"
          :readonly="isReadonly"
          :aria-invalid="errors?.length ? 'true' : 'false'"
          :class="$style['form-control']"
          :style="{
            resize: manualResize ? 'vertical' : 'none',
          }"
          v-on="externalListeners"
        />
      </div>

      <Transition name="fade-transition" mode="out-in">
        <slot v-if="loading" name="loading" :icon-class="$style.icon">
          <ThemeLoader size="1.75em" :class="$style.icon" />
        </slot>
        <slot v-else name="append" :icon-class="$style.icon" />
      </Transition>
    </div>

    <slot name="bottom" />

    <slot v-if="!hideErrors" name="error-state" v-bind="{ errors }">
      <Transition name="fade-transition" mode="in-out">
        <span v-if="errors?.length" class="theme-text-description--m" :class="$style['error-text']" v-text="errors[0]" />
      </Transition>
    </slot>
  </div>
</template>

<style lang="scss" module>
@import '@/styles/_media';

.container {
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;
}

.content {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wrapper {
  /* TODO: переместить в токены? */
  --input-background-color: var(--bnw100);

  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 18px;
  border-radius: 12px;

  font-size: v-bind('`${targetFontSize}px`');
  font-weight: 500;
  line-height: 1.6;

  min-height: 42px;

  transition: all 150ms ease-in-out;

  background-color: var(--input-background-color);
  color: var(--input-text-color, inherit);
}

/* однострочный textarea должен выглядеть как input */
.wrapper:has(textarea[rows]) {
  padding: 10px 18px 5px;
}
.wrapper:has(textarea[rows='1']) {
  padding: 6px 18px;
}

.wrapper:has(.form-control:hover:read-write) {
  --input-background-color: var(--bnw200);
}

.wrapper:has(.form-control:read-write:active),
.wrapper:has(.form-control:read-write:focus) {
  --input-background-color: var(--bnw300);
}

.wrapper:has(.form-control:read-only) {
  /* TODO: maybe opacity or oklch fn? */
  --input-text-color: var(--bnw500);
}

.wrapper:has(.form-control:disabled),
.form-control:disabled {
  cursor: not-allowed;
}

.wrapper:has(.form-control:read-only),
.form-control:read-only {
  cursor: default;
}

.wrapper:has(.form-control[aria-invalid='true']) {
  --input-background-color: var(--error100);
}

input,
textarea {
  min-width: 0;
  flex-grow: 1;
  font-weight: 500;
}

textarea {
  min-height: 1lh;
  max-height: 15lh;
}

@include ios-device {
  // трюк с увеличением шрифта для отключения авто-зума на ios (при сохранении дизайна)
  input,
  textarea {
    font-size: v-bind('`${iosZoomThreshold}px`');
    transform: scale(v-bind(fontFactor));
    transform-origin: left center;
    min-width: calc(100% / v-bind(fontFactor));
    margin-right: calc(100% * v-bind('fontFactor - 1'));
  }
}

textarea:focus-visible {
  outline: 0;
}

.form-control:focus {
  outline: 0;
}

.form-control::placeholder {
  color: var(--input-text-color, var(--bnw600));
}

.form-control {
  cursor: text;
  color: var(--input-text-color);
}
input.form-control[type='number'] {
  -moz-appearance: textfield;
}
input.form-control::-webkit-outer-spin-button,
input.form-control::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.icon {
  flex-shrink: 0;
  line-height: 0;
  width: 1.75em;
  aspect-ratio: 1/1;

  color: inherit;
}

.error-text {
  padding-left: 1em;
  color: var(--error);
}
</style>
